<script lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue';
import { LazyCollectionSelect } from '#layers/data-model/lazy';
import { deserializeFilter, getCanonicalCollectionRoute, getCollectionPrimaryKeyFields, mergeAnd, pruneIncompleteFilter, serializeFilter, useCollection, useDataModelDisplayNames } from '#layers/data-model/public';

const __DsBlock = {
	type: 'container',
	icon: 'lucide:list',
	name: '$t:blocks.listing.name',
	description: '$t:blocks.listing.description',
	category: 'basic',
	events: [
		{
			name: 'open',
			payload: [
				{
					name: 'id',
					type: 'object',
					array: false,
					required: true,
				},
			],
		},
		{
			name: 'add',
			payload: [],
		},
		{
			name: 'emptyStateAction',
			payload: [],
		},
	],
	options: ({ form, context }) => form([
		{
			name: 'collectionId',
			label: '$t:blocks.common.props.collection',
			render: LazyCollectionSelect,
			hidden: () => blockContextIsListingPage(context),
		},
		{
			name: 'title',
			label: '$t:blocks.common.props.title',
		},
		{
			name: 'description',
			label: '$t:blocks.common.props.description',
		},
		{
			name: 'detailPageOnOpen',
			label: '$t:blocks.listing.props.detail_page_on_open',
		},
		{
			name: 'showAddItem',
			label: '$t:blocks.listing.props.show_add_item',
		},
		{
			name: 'detailPageOnAdd',
			label: '$t:blocks.listing.props.detail_page_on_add',
		},
		{
			name: 'canFilter',
			label: '$t:blocks.listing.props.can_filter',
		},
		{
			name: 'canEditDisplayOptions',
			label: '$t:blocks.listing.props.can_edit_display_options',
		},
		{
			name: 'selectionMode',
			label: '$t:blocks.listing.props.selection_mode',
			type: 'enum',
			values: ['single', 'multiple'],
			block: BuiltinBlockIds.InputTileRadio,
			props: {
				items: [
					{
						value: 'single',
						label: '$t:blocks.listing.selection_modes.single',
						icon: 'lucide:square-check',
					},
					{
						value: 'multiple',
						label: '$t:blocks.listing.selection_modes.multiple',
						icon: 'lucide:list-checks',
					},
				],
			},
		},
		{
			name: 'selection',
			label: '$t:blocks.common.props.selection',
			model: true,
		},
	]),

	slots: [
		{
			name: 'actions',
		},
	],

	locked: blockContextIsListingPage,

	hidden: blockContextIsContentPage,
}</script>

<script lang="ts" setup>
import type { CrudActionType } from '#layers/access/public';
import type { ValueOf } from '#layers/base/public';
import type { CompositePrimaryKey, GlobalCollectionId } from '#layers/data-model/public';
import type { LayoutEmptyState, ListingLayoutOption, ListingView, SaveButtonType, SelectionMode } from '#layers/listing/public';
import { reactivePick } from '@vueuse/core';
import { omit } from 'lodash-es';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { useI18n } from 'vue-i18n';
import { useRouter } from '#app/composables/router';
import { canEditDataModel, CrudAction, useAuthorization, useEntitlement, usePermissionsStore } from '#layers/access/public';
import { blockContextIsContentPage, blockContextIsListingPage, blockRefKey, defineBlock, useBlockContext, useBlockInEditorContext, useBlockLibraryStore } from '#layers/block-engine/public';
import { useListingPageContext } from '#layers/blocks/composables/block-context/use-listing-page-context';
import { useTranslationUtils } from '#layers/i18n/public';
import { ButtonCreateItem, Listing, ListingSaveActions, ListingViews, useListingViews } from '#layers/listing/public';
import { secondaryHeader, showConfirmationDialog } from '#layers/ui/public';
import { useCollectionRoutes, useWorkspaceSchemaStore } from '#layers/workspace/public';

export type DefaultViewOptions = Pick<ListingView, 'layoutId' | 'displayOptions' | 'name' | 'icon'>;

interface ListingBlockProps {
	canBulkDelete?: boolean;
	canEditDisplayOptions?: boolean;
	canFilter?: boolean;
	description?: string;
	title?: string;
	deleteLabels?: {
		button?: string;
		dialog?: {
			title?: string;
			description?: string;
			confirm?: string;
		};
		success: {
			title?: string;
		};
	};
	/**
	 * The collection to fetch items from.
	 */
	collectionId?: GlobalCollectionId;

	selectionMode?: SelectionMode;

	/*
	* The default layout to use when a new view is created
	* default to LayoutTable
	*/
	defaultView?: DefaultViewOptions;
	/**
	 * Auto redirect to detail page on item open
	 */
	detailPageOnOpen?: boolean;
	/**
	 * Show the "Add Item" button
	 */
	showAddItem?: boolean;
	/**
	 * Auto redirect to detail page on item add
	 */
	detailPageOnAdd?: boolean;
	/**
	 * Custom empty state shown by the layout when there are no items and no active filters.
	 */
	emptyState?: LayoutEmptyState;
}

const props = withDefaults(defineProps<ListingBlockProps>(), {
	canFilter: true,
	canEditDisplayOptions: true,
	// Vue casts an absent Boolean prop to `false`; the explicit `undefined` default keeps "not passed"
	// distinct from an explicit `false`, so non-overriding listings fall through to `collectionCan`.
	canBulkDelete: undefined,
});

const emit = defineEmits<{
	open: [id: CompositePrimaryKey];
	add: [];
	emptyStateAction: [];
}>();

;

const forwardedListingProps = reactivePick(props, [
	'canEditDisplayOptions',
	'canFilter',
	'description',
	'deleteLabels',
]);

const selection = defineModel<CompositePrimaryKey[]>('selection', { default: () => [] });

const router = useRouter();
const { t } = useI18n();
const { translateObject } = useTranslationUtils();
const { blockInEditor, setProp } = useBlockInEditorContext();
const blockEditorLibrary = useBlockLibraryStore();
const { filteredBlocks: listingLayoutBlocks } = blockEditorLibrary.useFilteredBlocks({ filter: (block) => block.type === 'listing-layout' });
const { emitOnRootBlock } = useBlockContext();
const listingPageContext = useListingPageContext();

const schemaStore = useWorkspaceSchemaStore();

const finalCollectionId = computed(() => props.collectionId || listingPageContext.collectionId || null);
const { collection } = useCollection(finalCollectionId);

// The schema is read synchronously, so an unknown collection is only conclusive once it is hydrated.
const isCollectionMissing = computed(() => !schemaStore.isRefreshing && !collection.value);
const { detailRoute } = useCollectionRoutes(finalCollectionId);
/**
 * Is this listing block the default listing for this listing page (i.e. does the collection ID match the page defined collection ID)
 */
const isListingPageListing = computed(() => listingPageContext.isListingPage && listingPageContext.collectionId === finalCollectionId.value);

const {
	currentView,
	currentViewId,
	effectiveViews,
	addView,
	duplicateView,
	deleteCurrentView,
	defaultView: computedDefaultView,
	currentFilter,
	currentSearchTerm,
	currentSharedDisplayOptions,
	differsFromSharedView,
	differsFromSharedDisplay,
	saveForEveryone,
	resetToShared,
	getViewName,
} = useListingViews({
	collectionId: finalCollectionId,
	keyPrefix: listingPageContext.persistenceKeyPrefix,
	defaultViewOverride: computed(() => props.defaultView),
	resolveLayoutName: (layoutId) => blockEditorLibrary.getBlock(layoutId)?.name || layoutId,
});

const saveButtonType = computed<SaveButtonType>(() => {
	if (!differsFromSharedView.value) return null;
	return 'dropdown';
});

const translatedProps = computed(() => translateObject(props, { keys: ['title', 'description'], pick: true }));
const { getCollectionDisplayName } = useDataModelDisplayNames();

const resolvedTitle = computed(
	() => translatedProps.value.title || (collection.value ? getCollectionDisplayName(collection.value) : undefined),
);

const filterForDisplay = computed({
	get: () => collection.value ? deserializeFilter(currentFilter.value, collection.value) : { _and: [] },
	set: (value) => {
		if (collection.value) {
			currentFilter.value = serializeFilter(value, collection.value);
		}
	},
});

const mergedFilter = computed(() => {
	const pageFilter = listingPageContext.filter ?? { _and: [] };
	const applied = pruneIncompleteFilter(filterForDisplay.value) ?? { _and: [] };
	return mergeAnd(applied, pageFilter);
});

const isDisplayOptionsOpen = ref(false);

const { isRootChild } = useBlockContext();

const fullPageMode = computed(() => (isRootChild() && listingPageContext.pageIsFullWidth));
const headerTheme = computed(() => secondaryHeader());

const currentViewName = computed({
	get: () => currentView.value.name,
	set: (value) => {
		currentView.value = { ...currentView.value, name: value };
	},
});

const currentViewIcon = computed({
	get: () => currentView.value.icon,
	set: (value) => {
		currentView.value = { ...currentView.value, icon: value };
	},
});

const currentViewLayoutId = computed({
	get: () => currentView.value.layoutId,
	set: (value) => {
		currentView.value = { ...currentView.value, layoutId: value };
	},
});

const currentViewDisplayOptions = computed({
	get: () => currentView.value.displayOptions,
	set: (value) => {
		currentView.value = { ...currentView.value, displayOptions: value };
	},
});

const resolvedViewName = computed(() => getViewName(currentView.value));
const layoutDisplayName = computed(() => getViewName({ ...currentView.value, name: undefined }));
const selectableLayoutOptions = computed<ListingLayoutOption[]>(() => listingLayoutBlocks.value.map((block) => ({
	id: block.id,
	name: block.name,
	icon: block.icon ?? undefined,
})));

function resolveLayoutOption(layoutId: string): ListingLayoutOption | undefined {
	const block = blockEditorLibrary.getBlock(layoutId);
	if (!block) return;

	return {
		id: block.id,
		name: block.name,
		icon: block.icon ?? undefined,
	};
}

function handleSaveView(options?: { asCopy?: boolean }) {
	duplicateView({ asCopy: options?.asCopy, fromCurrentState: true });

	if (options?.asCopy) {
		isDisplayOptionsOpen.value = true;
	}
}

async function handleSaveForEveryone() {
	const confirmed = await showConfirmationDialog({
		props: {
			title: t('listing.views.save.save_for_everyone_confirm.title', { name: resolvedViewName.value }),
			description: t('listing.views.save.save_for_everyone_confirm.description'),
			confirm: { label: t('listing.views.save.save_for_everyone') },
		},
	});

	if (confirmed) {
		saveForEveryone();
	}
}

function resetFilters() {
	currentFilter.value = { _and: [] };
}

// Root element
// This blockRefKey can be used to manually reference the root element of the block in the editor
// although it's recommended to have a single static root display:block element.

const rootRef = useTemplateRef('rootRef');

defineExpose({
	[blockRefKey]: rootRef,
});

// Open item event

function onOpen(id: CompositePrimaryKey) {
	emit('open', id);

	if (isListingPageListing.value) {
		emitOnRootBlock('open', id);

		// Redirect to detail edit page
		if (props.detailPageOnOpen && collection.value) {
			const route = detailRoute(id);

			if (route) {
				router.push(route);
			}
		}
	}
}

// Add item

function onAddItem() {
	emit('add');

	if (isListingPageListing.value) {
		emitOnRootBlock('add');

		// Redirect to detail create page
		if (props.detailPageOnAdd && collection.value) {
			const route = detailRoute('+');

			if (route) {
				router.push(route);
			}
		}
	}
}

// Empty state action

function onEmptyStateAction() {
	// Content listing pages reuse the same create flow as the "Add Item" toolbar button.
	if (isListingPageListing.value) {
		onAddItem();
		return;
	}

	emit('emptyStateAction');
}

// Init selection

watch(() => listingPageContext.selection, (value) => {
	if (value) {
		selection.value = value;
	}
}, {
	immediate: true,
});

watch(selection, (value) => {
	emitOnRootBlock('update:selection', value);
});

function handleUpdateDisplayOptions(displayOptions: Record<string, unknown> | undefined) {
	currentViewDisplayOptions.value = displayOptions;
}

const selectionMode = computed(() => props.selectionMode ?? listingPageContext.selectionMode);

const { can } = useAuthorization();

function collectionCan(action: CrudActionType, fallback: boolean): boolean {
	const resolvedCollection = collection.value;
	return resolvedCollection ? can({ collection: resolvedCollection, action }) : fallback;
}

// Collection-level delete (no id): for a covered scoped-delete collection this resolves to
// "can delete at least one"; the backend enforces per-id on bulk execution. A caller may pass
// `canBulkDelete` to gate on a dedicated entitlement (e.g. workspace members / service accounts).
const resolvedCanBulkDelete = computed(() => props.canBulkDelete ?? collectionCan(CrudAction.Delete, false));
const canRead = computed(() => collectionCan(CrudAction.Read, true));
const canCreate = computed(() => collectionCan(CrudAction.Create, false));
const canAddItem = computed(() => props.showAddItem && canCreate.value);

// Every listing query carries the primary key: the item-query builder re-injects it unconditionally, and
// each layout (table, cards, calendar) falls back to it when no other field is available. So if the primary
// key is unreadable, the query asks for a field the engine rejects and keeps retrying; render a clear empty
// state instead of mounting that doomed query.
const permissionsStore = usePermissionsStore();
const { can: checkEntitlement } = useEntitlement();

const hasQueryableFields = computed(() => {
	const resolvedCollection = collection.value;
	if (!resolvedCollection) return true;

	const primaryKeyFields = getCollectionPrimaryKeyFields(resolvedCollection);
	if (primaryKeyFields.length === 0) return true;

	return primaryKeyFields.every((field) => permissionsStore.canReadField(resolvedCollection.id, field.id));
});

const canEditDataModelFields = computed(() => checkEntitlement(canEditDataModel));

function openDataModelEditor() {
	if (collection.value) {
		router.push(getCanonicalCollectionRoute(collection.value));
	}
}

const ListingGuardKind = { NotFound: 'not-found', NoAccess: 'no-access', NoFields: 'no-fields' } as const;
type ListingGuardKind = ValueOf<typeof ListingGuardKind>;

interface ListingGuardState {
	kind: ListingGuardKind;
	title: string;
	description: string;
	actionLabel?: string;
	onAction?: () => void;
}

// Resolve the first matching guard state (order sets precedence) or null to render the listing.
const guardState = computed<ListingGuardState | null>(() => {
	if (isCollectionMissing.value) {
		return {
			kind: ListingGuardKind.NotFound,
			title: t('blocks.listing.collection_not_found'),
			description: t('blocks.listing.collection_not_found_description'),
		};
	}

	if (!canRead.value) {
		return {
			kind: ListingGuardKind.NoAccess,
			title: t('permissions.no_read_access_title'),
			description: t('permissions.no_read_access'),
		};
	}

	if (!hasQueryableFields.value) {
		return {
			kind: ListingGuardKind.NoFields,
			title: t('blocks.listing.no_queryable_fields_title'),
			description: canEditDataModelFields.value
				? t('blocks.listing.no_queryable_fields_description')
				: t('blocks.listing.no_queryable_fields_readonly_description'),
			actionLabel: canEditDataModelFields.value ? t('common.add_field') : undefined,
			onAction: openDataModelEditor,
		};
	}

	return null;
});

function handleEmptyAction() {
	guardState.value?.onAction?.();
}

const translatedEmptyState = computed(() =>
	translateObject(props.emptyState, { keys: ['description', 'actionLabel'] }),
);

const resolvedEmptyState = computed(() => {
	const state = translatedEmptyState.value;
	if (!state) return;

	// The listing-page empty-state CTA creates an item; hide it when the user can't create one.
	if (isListingPageListing.value && !canAddItem.value) {
		return omit(state, ['actionLabel']);
	}

	return state;
});
</script>

<template>
	<div
		v-if="!finalCollectionId"
		ref="rootRef"
		class="p-4 font-medium"
	>
		<template v-if="blockInEditor">
			{{ $t('blocks.listing.select_collection') }}
		</template>
		<template v-else>
			{{ $t('blocks.listing.missing_collection') }}
		</template>
	</div>

	<DEmpty
		v-else-if="guardState"
		ref="rootRef"
		fill
		:title="guardState.title"
		:description="guardState.description"
		:action-button-label="guardState.actionLabel"
		@action="handleEmptyAction"
	>
		<template v-if="guardState.kind === ListingGuardKind.NoAccess && canAddItem" #action>
			<div class="w-full">
				<ButtonCreateItem
					:collection-id="finalCollectionId"
					@click="onAddItem"
				/>
			</div>
		</template>
	</DEmpty>

	<Listing
		v-else
		ref="rootRef"
		v-bind="forwardedListingProps"
		v-model:filter="filterForDisplay"
		v-model:search-term="currentSearchTerm"
		v-model:display-options="currentViewDisplayOptions"
		v-model:layout-id="currentViewLayoutId"
		v-model:view-name="currentViewName"
		v-model:view-icon="currentViewIcon"
		v-model:display-options-open="isDisplayOptionsOpen"
		v-model:selection="selection"
		:view-id="currentViewId"
		:display-has-changes="differsFromSharedDisplay"
		:shared-display-options="currentSharedDisplayOptions"
		:layout-display-name
		:layout-options="selectableLayoutOptions"
		:collection-id="finalCollectionId"
		:title="resolvedTitle"
		:show-selection-actions="!listingPageContext.pageDisplayMode"
		:class="{
			'rounded-none! border-none!': fullPageMode,
		}"
		:can-bulk-delete="resolvedCanBulkDelete"
		@save-for-everyone="handleSaveForEveryone"
		@reset-view="resetToShared"
	>
		<template #title>
			<BlockInteractiveZone v-if="blockInEditor">
				<DInput
					:model-value="resolvedTitle"
					:placeholder="$t('blocks.listing.new_listing')"
					:ui="{ base: ['rounded-none py-0 ps-0', headerTheme.title()] }"
					variant="none"
					:autofocus="!resolvedTitle"
					@update:model-value="setProp('title', $event)"
				/>
			</BlockInteractiveZone>
			<template v-else>
				{{ resolvedTitle }}
			</template>
		</template>
		<template #views>
			<ListingViews
				v-model:current-view-id="currentViewId"
				:views="effectiveViews"
				:default-view="computedDefaultView"
				:resolve-layout-option
				@add="(id) => {
					addView(id);
					isDisplayOptionsOpen = true;
				}"
				@edit="() => {
					isDisplayOptionsOpen = true;
				}"
				@duplicate="() => {
					duplicateView();
					isDisplayOptionsOpen = true;
				}"
				@delete-view="deleteCurrentView"
			/>
		</template>
		<template #default="{ layoutProps, searchTerm }">
			<BlockRender
				v-if="currentView && layoutProps.collection"
				:key="`${finalCollectionId}-${currentView.layoutId}`"
				:block="currentView.layoutId"
				:props="{
					...layoutProps,
					selectionMode,
					searchTerm,
					onOpen,
					'emptyState': resolvedEmptyState,
					'filter': mergedFilter,
					'selectionKeys': listingPageContext.selectionKeys,
					'projectionKeys': listingPageContext.projectionKeys,
					'selection': selection,
					'onUpdate:displayOptions': handleUpdateDisplayOptions,
					'onUpdate:selection': (value: CompositePrimaryKey[]) => selection = value,
					'onResetFilters': resetFilters,
					'onEmptyStateAction': onEmptyStateAction,
					'canResetView': differsFromSharedView,
					'onResetView': resetToShared,
				}"
			>
				<template v-if="$slots.actions" #actions>
					<BlockMarkHorizontalLayout>
						<slot name="actions" />
					</BlockMarkHorizontalLayout>
				</template>
			</BlockRender>
		</template>
		<template #filter-actions>
			<ListingSaveActions
				:save-button-type
				@save="handleSaveView"
				@save-to-view="handleSaveForEveryone"
				@save-as-new="handleSaveView({ asCopy: true })"
				@reset="resetToShared"
			/>
		</template>
		<template #actions>
			<BlockMarkHorizontalLayout>
				<slot name="actions" />
			</BlockMarkHorizontalLayout>

			<ButtonCreateItem v-if="showAddItem" :collection-id="finalCollectionId" @click="onAddItem" />
		</template>
	</Listing>
</template>
